import { Stack, Tab, Tabs } from '@mui/material'
import { useState } from 'react'

import { ArtistBannerDetailFragment, Maybe } from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { FileUploadInfo } from '@/components/pages/ConsoleCollectionCreate/template/CollectionCreateForm'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

import {
  ConsoleCampaignBannerSettingForm,
  SubmitValues,
} from './ConsoleCampaignBannerSettingForm'
import {
  ConsoleCampaignReservationBannerSettingForm,
  ReservationSubmitValues,
} from './ConsoleCampaignReservationBannerSettingForm'

export * from './fragments'

export type State = {
  artistId: string
  progress: number
  loading: boolean
  bannerSettings: Maybe<ArtistBannerDetailFragment[]>
  bannerSettingsReservation: Maybe<ArtistBannerDetailFragment[]>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onDrop: FileUploadInfo['onDrop']
  onSubmit: (
    values: SubmitValues | ReservationSubmitValues,
    isReservationBanners?: boolean
  ) => Promise<void>
  onTabChange: () => void
}

export const ConsoleCampaignBannerSettingTemplate: Template<State, Action> = ({
  state,
  onDrop,
  onSubmit,
  onTabChange,
}) => {
  const { t } = useTranslation('consoleCampaignBannerSetting')
  const { routerNames, routerPages } = useRouter()
  const [tab, setTab] = useState(0)

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue)
    onTabChange()
  }

  const isReservationBanners = tab === 1

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleCampaignBannerSetting} />
      <ConsoleBaseLayout
        margin
        header={{
          back: true,
          defaultBackPage: routerPages.ConsoleDashboard,
          title: t('title'),
        }}
      >
        <Stack>
          <Stack sx={{ pb: 6 }}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}
              variant="fullWidth"
            >
              <Tab
                label={t('tabPublicBanner', '公開バナー')}
                sx={{ color: 'text.primary' }}
              />
              <Tab
                label={t('tabReservationBanner', '予約バナー')}
                sx={{ color: 'text.primary' }}
              />
            </Tabs>
          </Stack>
        </Stack>
        {isReservationBanners ? (
          <ConsoleCampaignReservationBannerSettingForm
            loading={state.loading}
            bannerSettings={state.bannerSettingsReservation}
            fileUploadInfo={{ progress: state.progress, onDrop }}
            onSubmit={onSubmit}
            isReservationBanners={isReservationBanners}
            publicBannerStartedAt={state.bannerSettings?.[0]?.bannersStartedAt}
            publicBannerEndedAt={state.bannerSettings?.[0]?.bannersEndedAt}
          />
        ) : (
          <ConsoleCampaignBannerSettingForm
            loading={state.loading}
            bannerSettings={state.bannerSettings}
            fileUploadInfo={{ progress: state.progress, onDrop }}
            onSubmit={onSubmit}
            isReservationBanners={isReservationBanners}
            reservationBannerStartedAt={
              state.bannerSettingsReservation?.[0]?.bannersStartedAt
            }
            reservationBannerEndedAt={
              state.bannerSettingsReservation?.[0]?.bannersEndedAt
            }
          />
        )}
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}
